import { Box, Link, styled } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { type Dispatch, type FC, type SetStateAction, type UIEvent, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CommunicationTextField from "../../../components/StaffCommunication/util/CommunicationTextField";
import { NurseChatDialog } from "../../../components/StaffCommunication/util/Direct/NurseChatDialog";
import SystemDialog from "../../../components/UIToolkit/Popups/Dialog";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useIsMobile } from "../../../hooks/useMobile";
import { useFormatPersonName } from "../../../hooks/useNameFormat";
import { useSyncNursePto } from "../../../hooks/useSyncNursePto";
import { backendService } from "../../../services/backend.service";
import { INK_BLACK, SURFACE } from "../../../theme/tokens";
import { type NurseWithExternalData, ProfileMode } from "../../../utils/models";
import GeneralProfileDetails from "./GeneralDetails/GeneralProfileDetails";
import JobChangeDialog from "./GeneralDetails/JobChangeDialog";
import { useGeneralProfileDetailsState } from "./GeneralDetails/useGeneralProfileDetailsState";
import { Integrations } from "./Integrations/Integrations";
import { MergeDuplicateCard } from "./MergeDuplicate/MergeDuplicateCard";
import { NurseSchedulePreview } from "./NurseSchedulePreview/NurseSchedulePreview";
import { ProfessionalInfo } from "./ProfessionalInfo/ProfessionalInfo";
import { ProfileTopBar } from "./ProfileTopBar/ProfileTopBar";
import { QualificationsAndCertifications } from "./QualificationsAndCertifications/QualificationsAndCertifications";
import { RemoveFromDepartment } from "./RemoveFromDepartment/RemoveFromDepartment";
import { ScheduleSection } from "./ScheduleSection/ScheduleSection";
import { StaffEngagementSection } from "./StaffEngagementSection/StaffEngagementSection";
import { SynergyCredentials } from "./SynergyCredentials/SynergyCredentials";
import { useProfileV2State } from "./useProfileV2State";
import { SkeletonWithRandomWidths } from "./util/constants";

const Card = styled("div")({
	border: `1px solid ${alpha(INK_BLACK, 0.1)}`,
	boxShadow: `0px 4px 8px 0px ${alpha(INK_BLACK, 0.03)}`,
	background: SURFACE,
	display: "flex",
	padding: "14px 16px",
	alignItems: "center",
	justifyContent: "center",
	width: "100%",
	height: "fit-content",
	borderRadius: "8px",
});

type Props = { initialProfileMode: ProfileMode };

export const ProfileV2: FC<Props> = ({ initialProfileMode }) => {
	const formatPersonName = useFormatPersonName();
	const {
		nurseData,
		setNurseData,
		profileMode,
		imageDataString,
		isEditable,
		handleOnBackClick,
		backLabel,
		onEditClick,
		toggleEditCancelWarning,
		showEditCancelWarning,
		handleEditCancelConfirmed,
		onDeletePtoConfirmed,
		isDeletingTimeOff,
		isLoadingProfile,
		isProfileHydrating,
		refetchProfile,
		isAddingNurseLoading,
		isEditNurseLoading,
		createUserWhenCreatingNurse,
		setCreateUserWhenCreatingNurse,
		setDisableSaveDueToAnIssueCreatingUser,
		isFormValid,
		isAnyDemo,
		chosenDepartment,
		handleImageChanged,
		isPostProfileImageLoading,
		showWelcomeDialog,
		welcomeMessage,
		setWelcomeMessage,
		handleSaveClickWithDemoDialog,
		handleWelcomeDialogClose,
		jobNotificationDraft,
		pendingJobChange,
		handleJobChangeCancel,
		handleJobChangeApply,
		isApplyingJobChange,
		duplicateMatches,
		handleDuplicateCancel,
		handleDuplicateConfirmCreate,
		hasUnsavedEdits,
		handleNurseMerged,
	} = useProfileV2State({ initialProfileMode });
	const { t } = useTranslation();
	const { isMedicalDepartment, isRealDemo } = useHospitalData();

	const canSyncNursePto =
		profileMode === ProfileMode.VIEW && !!nurseData?.id && (chosenDepartment?.hrisIntegrations?.some((i) => i.type === "time_off" && !i.file) ?? false);

	const { mutateAsync: syncNursePto, isPending: isSyncingNursePto } = useSyncNursePto(nurseData?.id);

	const { data: allJobs } = useQuery({
		queryKey: ["jobs"],
		queryFn: async () => (await backendService.getJobs()).data,
		staleTime: 5 * 60 * 1000,
		enabled: !!pendingJobChange,
	});

	const resolveJobName = useCallback(
		(jobId: string) => {
			const job = (nurseData.jobs ?? []).find((j) => j.id === jobId) ?? allJobs?.find((j) => j.id === jobId);
			return job?.displayName ?? "";
		},
		[nurseData.jobs, allJobs],
	);

	const removedJobNames = useMemo(() => (pendingJobChange ? pendingJobChange.removedJobIds.map(resolveJobName) : []), [pendingJobChange, resolveJobName]);

	const newPrimaryJobName = useMemo(() => {
		const newPrimaryJobId = pendingJobChange?.newJobIds[0];
		return newPrimaryJobId ? resolveJobName(newPrimaryJobId) : "";
	}, [pendingJobChange, resolveJobName]);

	const { isMobile } = useIsMobile();
	const [showChat, setShowChat] = useState(false);

	// The top bar repeats the name only once the header card's copy has scrolled away, so it is never on
	// screen twice. Driven by scroll events alone so it starts hidden rather than flashing in until a
	// first measurement lands. Edit/new modes show name inputs instead of that row, so the top bar keeps it.
	const headerNameRef = useRef<HTMLElement>(null);
	const [isHeaderNameScrolledAway, setIsHeaderNameScrolledAway] = useState(false);
	const handleProfileScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
		const headerName = headerNameRef.current;
		// No name row while loading or in edit mode — nothing to compare against.
		if (!headerName) return;
		setIsHeaderNameScrolledAway(headerName.getBoundingClientRect().bottom <= event.currentTarget.getBoundingClientRect().top);
	}, []);
	const isTopBarNameVisible = profileMode !== ProfileMode.VIEW || isHeaderNameScrolledAway;

	const { handleRemoveNurseClick, isRemovingNurse, isSupportUser } = useGeneralProfileDetailsState({
		nurse: nurseData as NurseWithExternalData,
		setNurseData: setNurseData as Dispatch<SetStateAction<NurseWithExternalData>>,
		departmentId: chosenDepartment.department_id,
		refetchProfileRequested: refetchProfile,
		createUserWhenCreatingNurse,
		setCreateUserWhenCreatingNurse,
		profileMode,
	});

	return (
		<Box
			data-testid="profile-root"
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "start",
				width: "100%",
				height: "100%",
				background: (theme) => alpha(theme.palette.primary.main, 0.04),
			}}
		>
			<ProfileTopBar
				profileMode={profileMode}
				backLabel={backLabel}
				onBackClick={handleOnBackClick}
				onEditCancelClick={toggleEditCancelWarning}
				onEditClick={onEditClick}
				onEditSaveClick={handleSaveClickWithDemoDialog}
				onChatClick={nurseData?.id ? () => setShowChat(true) : undefined}
				chatDisabled={!!nurseData?.id && !nurseData?.userId}
				saveEnabled={isFormValid}
				saveLoading={isAddingNurseLoading || isEditNurseLoading || isPostProfileImageLoading}
				canSyncPto={canSyncNursePto}
				isSyncingPto={isSyncingNursePto}
				onSyncPtoClick={canSyncNursePto ? () => syncNursePto() : undefined}
				recordActionsDisabled={isProfileHydrating}
				isNameVisible={isTopBarNameVisible}
				nurseName={nurseData?.firstName || nurseData?.lastName ? formatPersonName(nurseData) : undefined}
			/>
			<Box
				onScroll={handleProfileScroll}
				sx={{
					overflow: "auto",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "flex-start",
					gap: "16px",
					width: "100%",
					height: "calc(100% - 65px)",

					padding: {
						sm: "10px 54px",
						xs: "10px 8px",
					},
				}}
			>
				<Card sx={{ justifyContent: "start" }}>
					{isLoadingProfile ? (
						<SkeletonWithRandomWidths avatar card />
					) : (
						<GeneralProfileDetails
							refetchProfileRequested={refetchProfile}
							nameRowRef={headerNameRef}
							nurse={nurseData as NurseWithExternalData}
							profileMode={profileMode}
							imageURL={imageDataString ?? undefined}
							setNurseData={setNurseData as Dispatch<SetStateAction<NurseWithExternalData>>}
							isEditable={isEditable}
							departmentId={chosenDepartment.department_id}
							createUserWhenCreatingNurse={createUserWhenCreatingNurse}
							setCreateUserWhenCreatingNurse={setCreateUserWhenCreatingNurse}
							setDisableSaveDueToAnIssueCreatingUser={setDisableSaveDueToAnIssueCreatingUser}
							handleImageChanged={handleImageChanged}
						/>
					)}
				</Card>
				{isMobile && profileMode === ProfileMode.VIEW && (
					<Card sx={{ p: 0 }}>
						{isLoadingProfile || !nurseData?.id ? <SkeletonWithRandomWidths card /> : <NurseSchedulePreview nurse={nurseData as NurseWithExternalData} />}
					</Card>
				)}
				<Card>
					{isLoadingProfile ? (
						<SkeletonWithRandomWidths randomLines />
					) : (
						<ProfessionalInfo
							nurse={nurseData as NurseWithExternalData}
							setNurseData={setNurseData as Dispatch<SetStateAction<NurseWithExternalData>>}
							isEditable={isEditable}
							showExtendedDemoGraphics={isAnyDemo || isRealDemo}
							jobNotificationDraft={jobNotificationDraft}
						/>
					)}
				</Card>

				{(isAnyDemo || isRealDemo) && !isLoadingProfile && profileMode !== ProfileMode.NEW && nurseData && (
					<Card>
						<StaffEngagementSection nurse={nurseData as NurseWithExternalData} />
					</Card>
				)}

				{!isMobile && profileMode !== ProfileMode.NEW && (
					<Card sx={{ flexDirection: "column", alignItems: "stretch", justifyContent: "flex-start", gap: 2 }}>
						<ScheduleSection
							nurse={nurseData as NurseWithExternalData}
							setNurseData={setNurseData as Dispatch<SetStateAction<NurseWithExternalData>>}
							onDeletePtoConfirmed={onDeletePtoConfirmed}
							isDeletingTimeOff={isDeletingTimeOff}
							isLoadingProfile={isLoadingProfile}
						/>
					</Card>
				)}
				{!isMobile && profileMode !== ProfileMode.NEW && isMedicalDepartment && (
					<Card>
						{isLoadingProfile ? (
							<SkeletonWithRandomWidths card />
						) : (
							<SynergyCredentials
								nurse={/*TODO, we should remove all the nurseData as ... from this code - its unsafe!!!*/ nurseData as NurseWithExternalData}
							/>
						)}
					</Card>
				)}
				{!isMobile && !isMedicalDepartment && (isLoadingProfile || (nurseData?.qualifications?.length ?? 0) > 0) && (
					<Card>
						{isLoadingProfile ? (
							<SkeletonWithRandomWidths card multipleCards={5} />
						) : (
							<QualificationsAndCertifications
								isEditable={isEditable}
								setNurseData={setNurseData as Dispatch<SetStateAction<NurseWithExternalData>>}
								nurse={nurseData as NurseWithExternalData}
							/>
						)}
					</Card>
				)}
				{isSupportUser && (nurseData.externalEmployees?.length ?? 0) > 0 && (
					<Card sx={{ flexDirection: "column", alignItems: "stretch", justifyContent: "flex-start", gap: 2 }}>
						<Integrations externalEmployees={nurseData.externalEmployees ?? []} />
					</Card>
				)}
				{isSupportUser && profileMode === ProfileMode.EDIT && !isProfileHydrating && (
					<Card>
						<MergeDuplicateCard
							nurse={{ id: nurseData.id, firstName: nurseData.firstName, lastName: nurseData.lastName }}
							hasUnsavedEdits={hasUnsavedEdits}
							onMerged={handleNurseMerged}
						/>
					</Card>
				)}
				{profileMode !== ProfileMode.NEW && (
					<Card>
						{isLoadingProfile ? (
							<SkeletonWithRandomWidths randomLines />
						) : (
							<RemoveFromDepartment nurse={nurseData as NurseWithExternalData} isLoading={isRemovingNurse} onRemoveNurseClick={handleRemoveNurseClick} />
						)}
					</Card>
				)}
			</Box>
			{showEditCancelWarning && (
				<SystemDialog
					data-testid="profile-cancel-edit-dialog"
					open={showEditCancelWarning}
					onClose={toggleEditCancelWarning}
					title={"Cancel edit?"}
					body={"All changes will be lost!"}
					onConfirm={handleEditCancelConfirmed}
					cancel={"No, keep editing"}
					confirm={"Yes, cancel editing"}
				/>
			)}

			{pendingJobChange && (
				<JobChangeDialog
					open={!!pendingJobChange}
					firstName={nurseData.firstName}
					jobNames={removedJobNames}
					newJobName={newPrimaryJobName}
					onClose={handleJobChangeCancel}
					onApply={handleJobChangeApply}
					applyLoading={isApplyingJobChange}
				/>
			)}

			{showChat && nurseData?.id && <NurseChatDialog open={showChat} nurse={nurseData as NurseWithExternalData} onClose={() => setShowChat(false)} />}

			{duplicateMatches.length > 0 && (
				<SystemDialog
					dataTestId="duplicate-nurse-warning"
					open={duplicateMatches.length > 0}
					onClose={handleDuplicateCancel}
					title={t("duplicate.nurse.warning.title")}
					body={
						<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
							<Box>{t("duplicate.nurse.warning.body")}</Box>
							<Box component="ul" sx={{ m: 0, pl: 2.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
								{duplicateMatches.map((match) => (
									<Box component="li" key={match.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<span>{formatPersonName(match)}</span>
										<Link
											data-testid={`duplicate-nurse-warning-view-${match.id}`}
											href={`/${chosenDepartment.department_id}/nurse/${match.id}`}
											target="_blank"
											rel="noopener noreferrer"
										>
											{t("snackbar.action.viewProfile")}
										</Link>
									</Box>
								))}
							</Box>
						</Box>
					}
					onConfirm={handleDuplicateConfirmCreate}
					cancel={t("cancel")}
					confirm={t("duplicate.nurse.warning.create.anyway")}
				/>
			)}

			{showWelcomeDialog && (
				<SystemDialog
					data-testid="profile-welcome-dialog"
					open={showWelcomeDialog}
					onClose={handleWelcomeDialogClose}
					title={"send a greeting message?"}
					body={
						<Box
							sx={{
								px: 1,
								py: 3,
								width: 550,
							}}
						>
							<CommunicationTextField maxLines={6} value={welcomeMessage} onChange={(value) => setWelcomeMessage(value)} onEnter={() => {}} />
						</Box>
					}
					onConfirm={handleWelcomeDialogClose}
					cancel={"Don't send"}
					confirm={"Send"}
				/>
			)}
		</Box>
	);
};
